import React, { ReactNode } from 'react'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  UniqueIdentifier,
  DragEndEvent,
} from '@dnd-kit/core'
import { Box } from '@mui/material'
import DragIcon from '@mui/icons-material/DragIndicator'

function SortableItem(props: { children: ReactNode; id: UniqueIdentifier }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {props.children}
    </div>
  )
}

export function SortableList({
  items,
  onOrderChange,
  renderItem,
}: {
  items: UniqueIdentifier[]
  onOrderChange: (newSortOrder: UniqueIdentifier[]) => void
  renderItem: (i: UniqueIdentifier) => ReactNode
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over) return

    if (active.id !== over.id) {
      const oldIndex = items.indexOf(active.id)
      const newIndex = items.indexOf(over.id)

      onOrderChange(arrayMove(items, oldIndex, newIndex))
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map((id) => (
          <SortableItem key={id} id={id}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{
                cursor: 'grab',
                backgroundColor: 'rgba(255, 255, 255, .8)',
              }}
              padding="5px"
              margin="2px"
              border={'solid 1px grey'}
            >
              {renderItem(id)}
              <DragIcon />
            </Box>
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  )
}
